import { readContract, writeContract, getBlock, getClient } from '@wagmi/core';
import {
  ContractFunctionExecutionError,
  type WriteContractReturnType,
  parseAbiItem,
  type Client,
  parseUnits,
} from 'viem';
import { getLogs } from 'viem/actions';
import contractJSON from '../../contracts/GIONodeSubOperator.json';
import gioSubContractJSON from '../../contracts/GIONodeSub.json';
import { config as wagmiConfig } from '@/wagmi';
import type {
  ESubIds,
  mintedGIOsTransactionDetailsType,
} from '~/types/GioNode';
import {
  ESplitDate,
  originalSubDailyRewardAmount,
  switch1SubDailyRewardAmount,
  switch2SubDailyRewardAmount,
  switch3SubDailyRewardAmount,
  switch4SubDailyRewardAmount,
} from '~/constants/prices';
import { getDirectAmount } from '~/utils/prices';

const contractAddress: `0x${string}` = contractJSON.address as `0x${string}`;

const abi = contractJSON.abi;

const calculateDailyReward = (sub: number, purchaseDate: Date) => {
  const DATE_2024_03_01 = new Date(ESplitDate.SWITCH1).getTime() / 1000;
  const DATE_2024_07_01 = new Date(ESplitDate.SWITCH2).getTime() / 1000;
  const DATE_2024_12_15 = new Date(ESplitDate.SWITCH3).getTime() / 1000;
  const DATE_2025_01_02 = new Date(ESplitDate.SWITCH4).getTime() / 1000;

  const PURCHASE_DATE = new Date(purchaseDate).getTime() / 1000;

  let totalReward = 0;

  if (PURCHASE_DATE < DATE_2024_03_01) {
    totalReward = originalSubDailyRewardAmount[sub as ESubIds];
  } else if (PURCHASE_DATE < DATE_2024_07_01) {
    totalReward = switch1SubDailyRewardAmount[sub as ESubIds];
  } else if (PURCHASE_DATE < DATE_2024_12_15) {
    totalReward = switch2SubDailyRewardAmount[sub as ESubIds];
  } else if (PURCHASE_DATE < DATE_2025_01_02) {
    totalReward = switch3SubDailyRewardAmount[sub as ESubIds];
  } else if (PURCHASE_DATE >= DATE_2025_01_02) {
    totalReward = switch4SubDailyRewardAmount[sub as ESubIds];
  }

  return totalReward;
};

export const mint = (
  toAddress: `0x${string}`,
  amount: number,
  sub: number,
  purchaseYear: number,
  purchaseMonth: number,
  purchaseDay: number,
): Promise<WriteContractReturnType> => {
  const reward = calculateDailyReward(
    sub,
    new Date(`${purchaseYear}/${purchaseMonth}/${purchaseDay}`),
  );
  const formattedReward = parseUnits(reward.toString(), 18);

  return writeContract(wagmiConfig, {
    abi,
    address: contractAddress,
    functionName: 'batchMint',
    args: [
      toAddress,
      amount,
      sub,
      formattedReward,
      purchaseYear,
      purchaseMonth,
      purchaseDay,
    ],
  });
};

export const makeDirectPayment = (
  toAddress: `0x${string}`,
  amount: number,
  sub: number,
  mintHash: `0x${string}`,
  purchaseDate: string,
): Promise<WriteContractReturnType> => {
  const directAmount = getDirectAmount(sub, purchaseDate);
  const totalDirectAmount = directAmount * Number(amount);

  const formattedTotalDirectAmount = parseUnits(
    totalDirectAmount.toString(),
    18,
  );
  return writeContract(wagmiConfig, {
    abi,
    address: contractAddress,
    functionName: 'transferGIO',
    args: [toAddress, formattedTotalDirectAmount, mintHash],
  });
};

export const burn = (
  sub: number,
  nftId: number,
): Promise<WriteContractReturnType> => {
  return writeContract(wagmiConfig, {
    abi,
    address: contractAddress,
    functionName: 'burn',
    args: [nftId, sub],
  });
};

export const transferToken = async (
  fromAddress: `0x${string}`,
  toAddress: `0x${string}`,
  sub: number,
  tokenId: number,
): Promise<WriteContractReturnType> => {
  const abi = gioSubContractJSON.abi;

  const activeSubs = await getActiveSubs(contractAddress);
  const subContractAddress = activeSubs[sub];

  return writeContract(wagmiConfig, {
    abi,
    address: subContractAddress,
    functionName: 'safeTransferFrom',
    args: [fromAddress, toAddress, tokenId],
  });
};

export const addHolder = (
  sub: number,
  holder: `0x${string}`,
): Promise<WriteContractReturnType> => {
  return writeContract(wagmiConfig, {
    abi,
    address: contractAddress,
    functionName: 'addHolder',
    args: [sub, holder],
  });
};

export const getAccountHoldings = async (
  address: `0x${string}`,
): Promise<Record<number, number[]> | undefined> => {
  if (!address) return;
  const holdings: Record<number, number[] | any> = {};

  const activeSubs = await getActiveSubs(contractAddress);

  const pms = Object.keys(activeSubs).map(async (sub) => {
    const result = (await readContract(wagmiConfig, {
      abi,
      address: contractAddress,
      functionName: 'tokenOfOwner',
      args: [address, sub],
    })) as ContractFunctionExecutionError | BigInt[];
    if (!(result instanceof ContractFunctionExecutionError)) {
      holdings[Number(sub)] = result?.map((v: any) => Number(v));
    } else {
      holdings[Number(sub)] = [];
    }
  });

  await Promise.allSettled(pms);

  return holdings;
};

const getActiveSubs = async (contractAddress: `0x${string}`) => {
  const activeSubsIdsBI = (await readContract(wagmiConfig, {
    abi,
    address: contractAddress,
    functionName: 'getAvailableSubs',
  })) as BigInt[];
  const activeSubsIds = activeSubsIdsBI.map((s) => Number(s));

  const activeSubs: Record<number, `0x${string}`> = {};

  await Promise.all(
    activeSubsIds.map(async (subId) => {
      const subContractAddress = (await readContract(wagmiConfig, {
        abi,
        address: contractAddress,
        functionName: 'subs',
        args: [subId],
      })) as `0x${string}`;
      activeSubs[subId] = subContractAddress;
    }),
  );

  return activeSubs;
};

const getAddressSubTransactionDetails = async (
  address: `0x${string}`,
  sub: number,
  subContract: `0x${string}`,
  publicClient: Client,
) => {
  const mintedGIOsTransactionHashes: mintedGIOsTransactionDetailsType = {};

  const ownedTokens = (await readContract(wagmiConfig, {
    abi,
    address: contractAddress,
    functionName: 'tokenOfOwner',
    args: [address, sub],
  })) as BigInt[];

  if (ownedTokens?.length < 1) return;

  const logs = await getLogs(publicClient, {
    address: subContract,
    event: parseAbiItem(
      'event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)',
    ),
    args: {
      tokenId: ownedTokens as bigint[],
    },
    fromBlock: 'earliest',
    toBlock: 'latest',
  });

  for (const logIndex in logs) {
    const tokenId = Number(logs[logIndex].args.tokenId);

    if (!mintedGIOsTransactionHashes[Number(sub)]) {
      mintedGIOsTransactionHashes[Number(sub)] = [];
    }
    mintedGIOsTransactionHashes[Number(sub)].push({
      tokenId,
      amount: 1,
      blockHash: logs[logIndex].blockHash,
      transactionHash: logs[logIndex].transactionHash,
    });
  }

  for (const key in mintedGIOsTransactionHashes) {
    const trans = mintedGIOsTransactionHashes[key];

    await Promise.all(
      trans.map((tran) => {
        return new Promise<void>((resolve) => {
          getBlock(wagmiConfig, {
            blockHash: tran.blockHash,
          }).then((block) => {
            tran.minted_at = new Date(
              Number(block.timestamp) * 1000,
            ).toUTCString();

            resolve();
          });
        });
      }),
    );
  }

  for (const key in mintedGIOsTransactionHashes) {
    const trans = mintedGIOsTransactionHashes[key];

    const pdPs = trans.map((tran) =>
      readContract(wagmiConfig, {
        abi,
        address: contractAddress,
        functionName: 'tokenDates',
        args: [sub, tran.tokenId],
      }),
    );

    const pdResults = (await Promise.all(pdPs)) as [];

    trans.forEach((tran, index) => {
      tran.purchaseDate = new Date(
        Number(pdResults[index][1] as BigInt) * 1000,
      ).toUTCString();
    });
  }

  // Get days rewarded
  for (const key in mintedGIOsTransactionHashes) {
    const trans = mintedGIOsTransactionHashes[key];

    const drPs = trans.map((tran) =>
      readContract(wagmiConfig, {
        abi,
        address: contractAddress,
        functionName: 'daysRewarded',
        args: [sub, tran.tokenId],
      }),
    );

    const drResults = (await Promise.all(drPs)) as [];

    trans.forEach((tran, index) => {
      tran.rewardedDays = Number(drResults[index]);
    });
  }

  return mintedGIOsTransactionHashes;
};

export const getNftsMintingInfo = async (
  address: `0x${string}`,
  subs: number[] = [],
) => {
  if (!address) return;

  const publicClient = getClient(wagmiConfig);

  // Grab active tokens
  const activeSubs = await getActiveSubs(contractAddress);

  type resultType = ReturnType<typeof getAddressSubTransactionDetails>;

  let mintedGIOsTransactionHashes: resultType = {} as resultType;

  const subsToGetFor = subs?.length > 0 ? subs : Object.keys(activeSubs);

  const result = await Promise.all(
    subsToGetFor?.map((sub) =>
      getAddressSubTransactionDetails(
        address,
        Number(sub),
        activeSubs[Number(sub)],
        publicClient as Client,
      ),
    ),
  );

  result.forEach((val) => {
    if (val) {
      mintedGIOsTransactionHashes = { ...mintedGIOsTransactionHashes, ...val };
    }
  });

  return mintedGIOsTransactionHashes;
};
